import { $isLinkNode } from '@lexical/link';
import { $isTextNode, $getRoot, LexicalNode } from 'lexical';
import _ from 'lodash';
import { createLexicalTextEditor } from 'modules/Lexical/editors';
import { $isCustomHeadingNode, CustomHeadingNode } from 'modules/Lexical/nodes/CustomHeadingNode';
import { CustomListItemNode, $isCustomListItemNode } from 'modules/Lexical/nodes/CustomListItemNode';
import { $isCustomListNode } from 'modules/Lexical/nodes/CustomListNode';
import { CustomParagraphNode, $isCustomParagraphNode } from 'modules/Lexical/nodes/CustomParagraphNode';
import { $isProjectMetadataTokenNode } from 'modules/Lexical/nodes/ProjectMetadataTokenNode';
import { $isReferenceCitationNode } from 'modules/Lexical/nodes/ReferenceCitationNode';
import { $isReferenceCitationSeparatorNode } from 'modules/Lexical/nodes/ReferenceCitationSeparatorNode';
import { HEADING_TAGS } from 'const';
import { excludeHtmlTag } from './common';
import { parseHtmlToJson, Node, TAGS, NODE_TYPES } from './parser';

const processChildNode = (childNode: LexicalNode, targetChild: Node | undefined): boolean => {
  if ($isProjectMetadataTokenNode(childNode)) {
    childNode.remove();

    return true;
  }

  if ($isReferenceCitationNode(childNode)) {
    return true;
  }

  if ($isTextNode(childNode) && targetChild?.type === NODE_TYPES.TEXT) {
    childNode.setTextContent(_.unescape(excludeHtmlTag(targetChild.data)));

    return true;
  }

  if ($isLinkNode(childNode) &&
    targetChild?.type === NODE_TYPES.TAG && targetChild?.name === TAGS.LINK) {
    childNode.setURL(excludeHtmlTag(targetChild.attribs?.href));
    childNode.getAllTextNodes()?.[0]?.setTextContent(_.unescape(targetChild?.children?.[0]?.data ?? ''));

    return true;
  }

  return false;
};

const inheritText = (node: CustomParagraphNode | CustomListItemNode | CustomHeadingNode, target: Node): boolean => {
  // should not touch project metadata tokens if this no text besides them
  if (node.getChildren().length === 0 || node.getChildren().every(childNode => $isProjectMetadataTokenNode(childNode))) {
    return true;
  }

  return node.getChildren().filter(childNode => !$isReferenceCitationSeparatorNode(childNode)).every((childNode, childIndex) =>
    processChildNode(childNode, target.children?.[childIndex]),
  );
};

const isMatchingTag = (targetNode: Node, expectedTag: string): boolean => {
  return targetNode.type === NODE_TYPES.TAG && targetNode.name === expectedTag;
};

const processNodePair = (lexicalNode: LexicalNode, targetNode: Node): boolean => {
  // Handle paragraph nodes
  if ($isCustomParagraphNode(lexicalNode) && isMatchingTag(targetNode, TAGS.PARAGRAPH)) {
    return inheritText(lexicalNode, targetNode);
  }

  // Heading tags
  if ($isCustomHeadingNode(lexicalNode) && HEADING_TAGS.some(headingTag => isMatchingTag(targetNode, headingTag))) {
    return inheritText(lexicalNode, targetNode);
  }

  // Handle list nodes
  if ($isCustomListNode(lexicalNode) && isMatchingTag(targetNode, TAGS.UNORDERED_LIST) && targetNode.children) {
    return lexicalNode.getChildren().every((childNode, childIndex) => {
      const targetListItem = targetNode.children?.[childIndex];

      return $isCustomListItemNode(childNode) &&
             targetListItem &&
             isMatchingTag(targetListItem, TAGS.LIST_ITEM) &&
             inheritText(childNode, targetListItem);
    });
  }

  return false;
};

export const shouldInheritInlineStyles = (
  sourceEditor: ReturnType<typeof createLexicalTextEditor>,
  textContent: string,
): boolean => {
  const targetJson: Node[] = parseHtmlToJson(textContent);
  let canInherit = true;

  sourceEditor.update(() => {
    const rootChildren = $getRoot().getChildren();

    if (rootChildren.length === 0) {
      canInherit = false;

      return;
    }

    canInherit = rootChildren.every((node, index) => {
      const targetNode = targetJson[index];

      return targetNode && processNodePair(node, targetNode);
    });
  }, { discrete: true });

  return canInherit;
};
